import { Injectable } from '@angular/core';
import { add, format, getMonth, isValid, parse, parseISO, setDate } from 'date-fns';
import { es } from 'date-fns/locale';

export enum FormatoFecha {
  DD_MM_YYYY = 'DD/MM/YYYY',
  DD_MM = 'DD/MM',
  MM = 'MM',
  MM_DD_YYYY = 'MM/DD/YYYY',
  YYYY_MM_DD = 'YYYY-MM-DD',
}

export enum FormatoFechaDateFns {
  DD_MM = 'dd/MM',
  DD_MM_YYYY = 'dd/MM/yyyy',
  MM = 'MM',
  MM_DD_YYYY = 'MM/dd/yyyy',
  YYYY_MM_DD = 'yyyy-MM-dd',
  YYYYMMDD = 'yyyyMMdd',
}

export enum UnidadTiempoDateFns {
  Years = 'years',
  Months = 'months',
  Weeks = 'weeks',
  Days = 'days',
  Hours = 'hours',
  Minutes = 'minutes',
  Seconds = 'seconds',
}

export enum SeparadorFecha {
  GUION = '-',
  SLASH = '/'
}

@Injectable({
    providedIn: 'root'
  })
export class DateTimeService {
  constructor() { }

  public newDate(): Date {
    return new Date();
  }

  public toString(date: Date) {
      const stringDate = String(date),
        year = stringDate.slice(0, 4),
        month = stringDate.slice(4, 6),
        day = stringDate.slice(6, 8);

      const dateEnd = day + '/' + month + '/' + year;

      return dateEnd;
    }

    public static calcularRangoFecha(date: Date, diffAge: number, dia = 0) {
      let maxDateB = date.getFullYear() - diffAge;
      let month = date.getMonth();
      let day = date.getDate() + dia;
      const newDate = new Date(maxDateB, month, day, 0, 0);
      return newDate;
    }

    public static agregarDiasFecha(date: Date, days: number): Date {
      date.setDate(date.getDate() + days);
      return date;
    }

  /**
   * Funcion para calcular la edad
   * @param dateA Fecha de nacimiento
   */
public static calcularEdad(dateA: Date | string): number {
  let birthDate: Date;

  if (typeof dateA === 'string') {
    const año = parseInt(dateA.substring(0, 4));
    const mes = parseInt(dateA.substring(4, 6)) - 1;
    const dia = parseInt(dateA.substring(6, 8));
    birthDate = new Date(año, mes, dia);
  } else {
    birthDate = new Date(dateA);
  }

  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const month = today.getMonth() - birthDate.getMonth();

  if (month < 0 || (month === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }

  return age;
}

  public static calcularEdadMapper(
    fechaNacimiento: Date | string,
    fechaReferencia: Date = new Date()
  ): number {
    let nacimiento: Date;

    if (typeof fechaNacimiento === 'string') {
      nacimiento = new Date(fechaNacimiento + 'T00:00:00');
      if (isNaN(nacimiento.getTime())) {
        throw new Error("Formato de fecha inválido. Usa 'yyyy-MM-dd'");
      }
    } else {
      nacimiento = new Date(fechaNacimiento);
    }

    const añoNac = nacimiento.getFullYear();
    const mesNac = nacimiento.getMonth();
    const diaNac = nacimiento.getDate();

    const añoHoy = fechaReferencia.getFullYear();
    const mesHoy = fechaReferencia.getMonth();
    const diaHoy = fechaReferencia.getDate();

    let edad = añoHoy - añoNac;

    // Si aún no ha cumplido años este año, restar 1
    if (mesHoy < mesNac || (mesHoy === mesNac && diaHoy < diaNac)) {
      edad--;
    }

    return edad;
  }


  public static formatearFecha(fecha: Date,
    formato: FormatoFecha = FormatoFecha.YYYY_MM_DD,
    separador: SeparadorFecha = SeparadorFecha.GUION): string {
    if (!fecha)
      return '';

    const year = fecha.getFullYear();
    const month = ('0' + (fecha.getMonth() + 1)).slice(-2);
    const day = ('0' + fecha.getDate()).slice(-2);

    switch (formato) {
      case FormatoFecha.DD_MM_YYYY:
        return `${day}${separador}${month}${separador}${year}`;
      case FormatoFecha.DD_MM:
        return `${day}${separador}${month}`;
      case FormatoFecha.YYYY_MM_DD:
        return `${year}${separador}${month}${separador}${day}`;
      case FormatoFecha.MM:
        return month;
      default:
        return `${day}/${month}/${year}`;
    }
  }

  //DateFns Fachade
  public agregarUnidad(cantidad: number, unidad: UnidadTiempoDateFns, establecerPrimerDiaDelMes = false, fechaOriginal: Date = null): Date {
    const unidadConCantidad = { [unidad]: cantidad };
    let fechaModificada = add(fechaOriginal, unidadConCantidad);

    if (establecerPrimerDiaDelMes) {
      const diaDelMesPorDefecto = 1;
      fechaModificada = setDate(fechaModificada, diaDelMesPorDefecto);
    }

    return fechaModificada;
  }

  public parsearFecha(fechaString: string, formato: FormatoFechaDateFns): Date {
    return parse(fechaString, formato, new Date());
  }

  public esFormatoDeFechaValido(fechaString: string, formato: string): boolean {
    try {
      const fechaParseada = parse(fechaString, formato, new Date());
      const fechaFormateada = format(fechaParseada, formato);
      return fechaString === fechaFormateada;
    } catch (error) {
      return false;
    }
  }

  public formatearMes(
    mes: number | null = null,
    formato: 'M' | 'MM' | 'MMM' |'MMMM',
    fecha: Date | null = null,
  ): string {
    const fechaBase = fecha ?? new Date();
    const mesBase = mes ?? getMonth(new Date());
    return format(
      add(fechaBase, { months: mesBase - getMonth(fechaBase) }),
      formato,
      { locale: es }
    );
  }

  public esFechaISO(fechaString: string): boolean {
    const fecha = parseISO(fechaString);
    return isValid(fecha);
  }

  public parseISO(fechaEnFormatoISO: string){
    return parseISO(fechaEnFormatoISO);
  }

  public esFechaValida(fecha: Date) {
    return isValid(fecha);
  }

  public static quitarHora(fechaStr: string): string {
    if (!fechaStr) return '';
    const date = new Date(fechaStr);
    if (isNaN(date.getTime())) return '';
    return date.toISOString().split('T')[0];
  }
}
